import { z } from "zod";
import type { SocialPlatform } from "@content-os/config";

// Mirrors the Prisma enum; kept in lockstep manually so this package has no
// dependency on @content-os/db.
export const POST_TYPES = [
  // Tier 1 — single image + caption
  "IG_FEED",
  "FB_FEED",
  "LINKEDIN_TEXT_OR_IMAGE",
  "X_TWEET",
  "THREADS_POST",
  "REDDIT_LINK",
  "REDDIT_MEDIA",
  "GBP_UPDATE",
  // Tier 2 — multi-media native
  "IG_CAROUSEL",
  "LINKEDIN_DOC",
  "X_THREAD",
  "THREADS_THREAD",
  "REDDIT_SELF",
  // Tier 3 — video
  "IG_REEL",
  "IG_STORY",
  "FB_REEL",
  "FB_STORY",
  "TIKTOK_VIDEO",
  "TIKTOK_PHOTO",
  "YOUTUBE_SHORT",
  "YOUTUBE_LONG",
] as const;

export type PostType = (typeof POST_TYPES)[number];

export type Tier = 1 | 2 | 3;

export interface MediaSpec {
  kind: "image" | "video" | "either" | "none";
  minItems: number;
  maxItems: number;
  /** width / height — the *preferred* aspect to crop to */
  preferredAspect: number;
  /** [min, max] aspect range that the platform accepts without re-encoding */
  allowedAspect: [number, number];
  /** max width in pixels for the platform's serving layer */
  maxWidth: number;
  /** max file size in bytes for the platform's ingest endpoint */
  maxBytes: number;
}

/** Transcode target for a video PostType. Consumed by the worker's
 * MediaConvert handler to produce a platform-friendly H.264/AAC MP4
 * derivative. Image-only PostTypes leave this undefined. */
export interface VideoSpec {
  width: number;
  height: number;
  /** H.264 max bitrate (kbps). MediaConvert uses QVBR with this as the cap. */
  maxBitrateKbps: number;
  /** Platform's hard cap on duration in seconds — informational; not enforced
   *  by the transcoder, but surfaced to the validator. */
  maxDurationSec: number;
  container: "mp4";
}

export interface CopySpec {
  captionMaxChars: number;
  titleMaxChars?: number;
  descriptionMaxChars?: number;
  /** When true, hashtags are managed as a separate array (vs inline in the caption) */
  hashtagsAllowed: boolean;
  /** When true, copy is split across an ordered list of items (threads) */
  threaded?: boolean;
  threadItemMaxChars?: number;
  threadMaxItems?: number;
}

/** Generation output shape for a single PostType — what the AI prompt returns. */
export const GenerationOutputSchema = z.object({
  caption: z.string().optional(),
  hashtags: z.array(z.string()).optional(),
  title: z.string().optional(),
  description: z.string().optional(),
  threadItems: z
    .array(
      z.object({
        content: z.string(),
      }),
    )
    .optional(),
  knobs: z.record(z.unknown()).optional(),
});
export type GenerationOutput = z.infer<typeof GenerationOutputSchema>;

/** Shape of the Piece row that publish adapters and validators consume. */
export interface PieceLike {
  id: string;
  postType: PostType;
  platform: SocialPlatform;
  caption: string;
  hashtags: string[];
  mediaUrls: string[];
  title: string | null;
  description: string | null;
  threadItems: Array<{ content: string; mediaUrls?: string[] }> | null;
  platformKnobs: Record<string, unknown>;
  /** Cover image for a video piece (Piece.thumbnailUrl); used as the Reel / video cover. */
  thumbnailUrl?: string | null;
}

export interface AccountLike {
  zernioAccountId: string;
  platform: SocialPlatform;
}

export interface PostTypeSpec {
  id: PostType;
  platform: SocialPlatform;
  label: string;
  tier: Tier;
  media: MediaSpec;
  copy: CopySpec;
  /** Transcode target. Present for PostTypes whose media.kind is "video". */
  video?: VideoSpec;
  /** True for the platform's 24h ephemeral Story PostType (IG_STORY, FB_STORY, …). */
  isStory?: boolean;
  /** Required knobs that must be present on the Piece before publish. */
  requiredKnobs?: string[];
}
